import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import SelectInput from '@components/atoms/ui-kit/select-input';
import toast from 'react-hot-toast';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';

// Types
import { CreateOneOffChargePayload } from '@utils/types/back-office/usage-revenue';
import { CustomerBo } from '@utils/types/back-office/customers';
import { BaseResponse } from '@utils/types/shared/base';
import { SupportedCurrency } from '@utils/types/organization/organization';

// Hooks
import { useCreateOneOffCharge } from '@utils/api/back-office/usage-revenue';

interface AddOneOffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  // Optional pre-selected values
  organizationId?: number;
  billingPeriodId?: number | null;
  currency?: SupportedCurrency;
}

// Hook to fetch organizations for the dropdown (independent of URL params)
const useOrganizationsDropdown = (searchQuery: string) => {
  return useQuery<BaseResponse<CustomerBo[]>>(
    ['organizationsDropdown', searchQuery],
    async () => {
      const params = new URLSearchParams({
        offset: '0',
        limit: '50'
      });
      if (searchQuery) {
        params.append('q', searchQuery);
      }
      const { data } = await Axios.get<BaseResponse<CustomerBo[]>>(
        `${URL_EXTENSION}/admin/customer?${params.toString()}`
      );
      return data;
    },
    {
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true
    }
  );
};

export default function AddOneOffModal({
  isOpen,
  onClose,
  onSuccess,
  organizationId: defaultOrgId,
  billingPeriodId: defaultPeriodId,
  currency = 'EUR'
}: AddOneOffModalProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [selectedOrg, setSelectedOrg] = useState<number | undefined>(
    defaultOrgId
  );
  const [billingPeriodId] = useState<number | null | undefined>(
    defaultPeriodId
  );
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<string>('');
  const [orgSearchQuery, setOrgSearchQuery] = useState('');

  // Fetch organizations for the dropdown
  const { data: customersData } = useOrganizationsDropdown(orgSearchQuery);

  // Create one-off charge mutation
  const { mutateAsync: createOneOffCharge, isLoading: isSubmitting } =
    useCreateOneOffCharge();

  // Reset selected org when defaultOrgId changes
  useEffect(() => {
    setSelectedOrg(defaultOrgId);
  }, [defaultOrgId]);

  const organizationOptions = useMemo(
    () =>
      (customersData?.result ?? []).map(org => ({
        value: String(org.id),
        label: org.name
      })),
    [customersData]
  );

  const handleOrgInputChange = useCallback((inputValue: string) => {
    setOrgSearchQuery(inputValue);
  }, []);

  const handleSubmit = async () => {
    if (!selectedOrg) {
      toast.error(t('please-select-organization'));
      return;
    }
    if (!description.trim()) {
      toast.error(t('description-is-required'));
      return;
    }
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      toast.error(t('please-enter-valid-amount'));
      return;
    }

    try {
      const payload: CreateOneOffChargePayload = {
        organization_id: selectedOrg,
        billing_period_id: billingPeriodId ?? null,
        description: description.trim(),
        amount_excl: parseFloat(amount)
      };
      await createOneOffCharge(payload);

      toast.success(t('one-off-line-created-successfully'));
      handleClose();
      onSuccess?.();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error_description?: string; detail?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.error_description ||
        err?.response?.data?.detail ||
        err?.message ||
        t('failed-to-create-one-off-line');
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setSelectedOrg(defaultOrgId);
    setDescription('');
    setAmount('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('add-one-off-charge')}
      caption={t('add-one-off-charge-description')}
      size="small"
      footerActions={[
        <Button
          key="cancel"
          label={t('cancel')}
          variant="secondary"
          onClick={handleClose}
          disabled={isSubmitting}
        />,
        <Button
          key="submit"
          label={isSubmitting ? t('creating') : t('add-charge')}
          onClick={handleSubmit}
          disabled={
            isSubmitting || !selectedOrg || !description.trim() || !amount
          }
        />
      ]}
    >
      <div className="space-y-4">
        {/* Organization Selector */}
        <div>
          <label
            className="block text-sm font-medium mb-1.5"
            style={{ color: getColour('normalText') }}
          >
            {t('organization')} <span className="text-red-500">*</span>
          </label>
          <SelectInput
            options={organizationOptions}
            value={
              selectedOrg
                ? organizationOptions.find(
                    o => o.value === String(selectedOrg)
                  ) || null
                : null
            }
            onChange={opt =>
              setSelectedOrg(opt ? Number(opt.value) : undefined)
            }
            onInputChange={handleOrgInputChange}
            placeholder={t('select-organization')}
            isClearable
            isSearchable
          />
        </div>

        {/* Description */}
        <div>
          <label
            className="block text-sm font-medium mb-1.5"
            style={{ color: getColour('normalText') }}
          >
            {t('description')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('enter-description')}
            required
            size="small"
          />
        </div>

        {/* Amount */}
        <div>
          <label
            className="block text-sm font-medium mb-1.5"
            style={{ color: getColour('normalText') }}
          >
            {t('amount')} ({t('excl-vat')}){' '}
            <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0.00"
            required
            size="small"
          />
        </div>

        {/* Summary */}
        {amount && parseFloat(amount) > 0 && (
          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('charge-amount')}
              </span>
              <span
                className="text-lg font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: 2
                }).format(parseFloat(amount))}
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
